
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { validateCanonical } from '@/utils/canonicalValidator.js';

export default function SEOHead({ 
  title, 
  description, 
  keywords, 
  canonicalUrl, 
  schema, 
  breadcrumbSchema,
  faqSchema,
  ogTitle,
  ogDescription,
  ogImage = "https://tudoconversor.com.br/og-image.png",
  ogType = "website",
  twitterTitle,
  twitterDescription,
  twitterImage = "https://tudoconversor.com.br/og-image.png",
  noIndex = false
}) {
  const location = useLocation();
  const currentUrl = `https://tudoconversor.com.br${location.pathname}`;

  // Dynamic OG locale: pt_BR for Portuguese routes, en_US for /en routes.
  const isEN = location.pathname.startsWith('/en');
  const ogLocale = isEN ? 'en_US' : 'pt_BR';

  const ptPath = location.pathname.replace(/^\/en(\/|$)/, '/') || '/';
  const enPath = location.pathname.startsWith('/en')
    ? location.pathname
    : `/en${location.pathname === '/' ? '' : location.pathname}`;

  // Truncate at a word boundary so titles/descriptions never end mid-word
  // (e.g. "TudoConversor" -> "TudoConver"). If a title is too long, prefer
  // dropping the trailing "| TudoConversor" brand suffix before cutting words.
  const truncateAtWord = (str, max) => {
    if (!str || str.length <= max) return str || '';
    let t = str.replace(/\s*\|\s*TudoConversor\s*$/, '').trim();
    if (t.length <= max) return t;
    const slice = t.slice(0, max);
    const lastSpace = slice.lastIndexOf(' ');
    return lastSpace > max * 0.5 ? slice.slice(0, lastSpace).trim() : slice.trim();
  };
  const finalTitle = title ? truncateAtWord(title, 60) : 'TudoConversor - Conversor Online Gratuito';
  const finalDescription = description ? truncateAtWord(description, 160) : '';
  const cleanCanonicalUrl = canonicalUrl ? canonicalUrl.split('?')[0] : undefined;
  const robotsContent = noIndex ? "noindex, follow" : "index, follow";

  useEffect(() => {
    if (cleanCanonicalUrl) {
      validateCanonical(cleanCanonicalUrl);
    }
  }, [cleanCanonicalUrl]);

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="TudoConversor" />
      <meta name="robots" content={robotsContent} />
      {cleanCanonicalUrl && <link rel="canonical" href={cleanCanonicalUrl} />}

      <link rel="alternate" hrefLang="pt-BR" href={`https://tudoconversor.com.br${ptPath}`} />
      <link rel="alternate" hrefLang="en" href={`https://tudoconversor.com.br${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`https://tudoconversor.com.br${ptPath}`} />

      <meta property="og:title" content={ogTitle || finalTitle} />
      <meta property="og:description" content={ogDescription || finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {cleanCanonicalUrl && <meta property="og:url" content={cleanCanonicalUrl} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="TudoConversor" />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tudoconversor" />
      <meta name="twitter:creator" content="@tudoconversor" />
      <meta name="twitter:title" content={twitterTitle || finalTitle} />
      <meta name="twitter:description" content={twitterDescription || finalDescription} />
      <meta name="twitter:image" content={twitterImage} />

      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
}
